import React from "react";


const CDN_TEAM_LOGO = (id) =>
    id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined;

const pad = (n) => String(n ?? "").padStart(2, "0");
const minuteLabel = (e) =>
    `${pad(e?.time?.elapsed)}${e?.time?.extra ? `+${e.time.extra}` : ""}'`;

const byElapsedThenExtra = (a, b) => {
    const ea = a?.time?.elapsed ?? 0, eb = b?.time?.elapsed ?? 0;
    if (ea !== eb) return ea - eb;
    const xa = a?.time?.extra ?? 0, xb = b?.time?.extra ?? 0;
    return xa - xb;
};

const sideOf = (evt, homeId) => (evt?.team?.id === homeId ? "home" : "away");

const IconGoal = () => <span className="mr-ico" aria-label="Goal">⚽</span>;
const IconOG = () => <span className="mr-ico" aria-label="Own goal">🟥⚽</span>;
const IconPen = () => <span className="mr-ico" aria-label="Penalty">🅿️</span>;
const IconMiss = () => <span className="mr-ico" aria-label="Missed penalty">❌</span>;
const IconVAR = () => <span className="mr-ico" aria-label="VAR" style={{ fontWeight: 900 }}>🖥️</span>;
const IconYC = () => <span className="mr-card mr-card--y" aria-label="Yellow card" />;
const IconRC = () => <span className="mr-card mr-card--r" aria-label="Red card" />;
const IconSY = () => (
    <span className="mr-cardstack" aria-label="Second yellow">
        <span className="mr-card mr-card--y" />
        <span className="mr-card mr-card--y mr-card--y2" />
    </span>
);

const IconSub = () => (
    <span className="mr-sub" aria-hidden="true">
        <span className="mr-sub-up">↑</span>
        <span className="mr-sub-dn">↓</span>
    </span>
);

function EventText({ e, align = "left" }) {
    const type = e?.type;
    const detail = (e?.detail || "").toLowerCase();
    const p = e?.player?.name || "";
    const a = e?.assist?.name || "";

    const cls = `mr-text ${align === "right" ? "mr-text--right" : ""}`;

    if (type === "subst" || detail.includes("substitution")) {
        return (
            <div className={cls}>
                <strong>{a || "Player In"}</strong>
                <div className="mr-subtext">({p || "Player Out"})</div>
            </div>
        );
    }

    if (type === "Card") {
        return (
            <div className={cls}>
                <strong>{p}</strong>
            </div>
        );
    }

    if (type === "Var") {
        return (
            <div className={cls}>
                <strong>{p || "VAR"}</strong>
                {e?.detail ? <div className="mr-subtext">{e.detail}</div> : null}
            </div>
        );
    }

    return (
        <div className={cls}>
            <strong>{p}</strong>
            {a ? <div className="mr-subtext">({a})</div> : null}
        </div>
    );
}

function EventIcon({ e }) {
    const t = e?.type;
    const d = (e?.detail || "").toLowerCase();

    if (t === "Card") {
        if (d.includes("second yellow")) return <IconSY />;
        if (d.includes("red")) return <IconRC />;
        return <IconYC />;
    }
    if (t === "Var") return <IconVAR />;
    if (t === "subst" || d.includes("substitution")) return <IconSub />;

    if (t === "Goal") {
        if (d.includes("own")) return <IconOG />;
        if (d.includes("missed")) return <IconMiss />;
        if (d.includes("penalty")) return <IconPen />;
        return <IconGoal />;
    }

    return <span className="mr-ico">•</span>;
}

function splitHalves(events = []) {
    const first = events.filter((e) => (e?.time?.elapsed ?? 0) <= 45).sort(byElapsedThenExtra);
    const second = events.filter((e) => (e?.time?.elapsed ?? 0) > 45).sort(byElapsedThenExtra);
    return { first, second };
}

export default function MatchReview({ fixture, homeId, awayId, events }) {
    const home = {
        id: homeId ?? fixture?.teams?.home?.id,
        name: fixture?.teams?.home?.name,
        logo: CDN_TEAM_LOGO(homeId ?? fixture?.teams?.home?.id),
    };
    const away = {
        id: awayId ?? fixture?.teams?.away?.id,
        name: fixture?.teams?.away?.name,
        logo: CDN_TEAM_LOGO(awayId ?? fixture?.teams?.away?.id),
    };

    const halfScore = `${fixture?.score?.halftime?.home ?? 0} - ${fixture?.score?.halftime?.away ?? 0}`;
    const fullScore = `${fixture?.score?.fulltime?.home ?? fixture?.goals?.home ?? 0} - ${fixture?.score?.fulltime?.away ?? fixture?.goals?.away ?? 0}`;

    const { first, second } = splitHalves(events);

    return (
        <section className="px-container">
            <div className="mr-wrap">
                <style>{css}</style>
                <h5 className="section-title mb-0">
                    {fixture?.teams?.home?.name} vs {fixture?.teams?.away?.name} — Match Review
                </h5>
                <p className="section-subtitle mb-3">
                    Minute-by-minute recap of goals, cards, substitutions and VAR decisions — split by halves with home events on the left and away on the right.
                </p>

                <div className="w-100 mr-cardwrap">
                    <div className="mr-head">
                        <div className="mr-team">
                            <img src={home.logo} alt="" aria-hidden="true" />
                            <span>{home.name}</span>
                        </div>
                        <div className="mr-team">
                            <img src={away.logo} alt="" aria-hidden="true" />
                            <span>{away.name}</span>
                        </div>
                    </div>

                    <div className="mr-body">
                        <div className="mr-sec">
                            <div className="mr-sec-h">
                                <span>1st Half</span>
                                <span className="mr-chip">{halfScore}</span>
                            </div>
                            <div className="mr-grid">
                                <div className="mr-col mr-col--home">
                                    {first.filter((e) => sideOf(e, home.id) === "home").map((e, i) => (
                                        <div className="mr-item" key={`fh-${i}`}>
                                            <div className="mr-time">{minuteLabel(e)}</div>
                                            <EventIcon e={e} />
                                            <EventText e={e} />
                                        </div>
                                    ))}
                                </div>
                                <div className="mr-divider" aria-hidden />
                                <div className="mr-col mr-col--away">
                                    {first.filter((e) => sideOf(e, home.id) === "away").map((e, i) => (
                                        <div className="mr-item mr-item--right" key={`fa-${i}`}>
                                            <EventText e={e} align="right" />
                                            <EventIcon e={e} />
                                            <div className="mr-time">{minuteLabel(e)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mr-sec">
                            <div className="mr-sec-h">
                                <span>2nd Half</span>
                                <span className="mr-chip">{fullScore}</span>
                            </div>
                            <div className="mr-grid">
                                <div className="mr-col mr-col--home">
                                    {second.filter((e) => sideOf(e, home.id) === "home").map((e, i) => (
                                        <div className="mr-item" key={`sh-${i}`}>
                                            <div className="mr-time">{minuteLabel(e)}</div>
                                            <EventIcon e={e} />
                                            <EventText e={e} />
                                        </div>
                                    ))}
                                </div>
                                <div className="mr-divider" aria-hidden />
                                <div className="mr-col mr-col--away">
                                    {second.filter((e) => sideOf(e, home.id) === "away").map((e, i) => (
                                        <div className="mr-item mr-item--right" key={`sa-${i}`}>
                                            <EventText e={e} align="right" />
                                            <EventIcon e={e} />
                                            <div className="mr-time">{minuteLabel(e)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
}

const css = `
.px-container { background:#f7f7f9; padding:30px; }
@media (max-width: 620px) { .px-container { padding:16px; } }
.mr-wrap {  max-width: 1380px; margin: 0 auto; }
.mr-head { display:flex; justify-content:space-between; gap:12px; margin-bottom:8px; flex-wrap:wrap; }
.mr-team { display:flex; align-items:center; gap:8px; color:#0f172a; font-weight:800; }
.mr-team img { width:20px; height:20px; object-fit:contain; }

.mr-error { padding:10px 12px; color:#991b1b; background:#fee2e2; border:1px solid #fecaca; border-radius:8px; }
.mr-skel { padding:2px 0; } .mr-skel-row { height:32px; border-radius:8px; background:linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 37%,#f1f5f9 63%); background-size:400% 100%; animation:mr-shimmer 1.2s infinite linear; margin:6px 0; }
@keyframes mr-shimmer { 0% {background-position: 100% 0} 100% {background-position: -100% 0} }

.mr-sec { margin-top:10px; }
.mr-sec-h {
  display:flex; align-items:center; justify-content:space-between;
  background:#f3f5fa; color:#0f172a; font-weight:800; border-radius:6px; padding:6px 10px; margin-bottom:10px;
}
.mr-chip { background:#fff; border:1px solid rgba(2,6,23,.12); border-radius:999px; padding:2px 8px; font-weight:900; }

/* Always 2 columns (home | divider | away) on ALL widths */
.mr-grid {
  display:grid;
  grid-template-columns: minmax(0,1fr) 2px minmax(0,1fr);
  gap:10px;
  align-items:start;
}
.mr-divider { width:2px; background:rgba(2,6,23,.06); min-height:100%; align-self:stretch; }

.mr-col { display:flex; flex-direction:column; gap:10px; }

/* Item layout: time | icon | text (and mirrored on right) */
.mr-item { display:grid; grid-template-columns: 48px 24px minmax(0,1fr); align-items:start; gap:8px; }
.mr-item--right { grid-template-columns: minmax(0,1fr) 24px 48px; text-align:right; }

.mr-time { color:#64748b; font-size:12px; font-weight:800; line-height:20px; white-space:nowrap; }

.mr-ico { width:24px; display:inline-flex; justify-content:center; align-items:flex-start; line-height:20px; }

/* Text block: subtext is always below */
.mr-text { display:flex; flex-direction:column; align-items:flex-start; gap:2px; min-width:0; }
.mr-text strong { display:block; line-height:1.25; overflow-wrap:break-word; word-break:break-word; hyphens:auto; }
.mr-subtext { color:#64748b; font-size:12px; line-height:1.25; overflow-wrap:break-word; word-break:break-word; }
.mr-text--right { align-items:flex-end; text-align:right; }

/* Cards & special icons */
.mr-card { display:inline-block; width:14px; height:18px; border-radius:2px; box-shadow:0 1px 0 rgba(0,0,0,.1); }
.mr-card--y { background:#facc15; border:1px solid #eab308; }
.mr-card--r { background:#ef4444; border:1px solid #dc2626; }
.mr-cardstack { position:relative; width:18px; height:18px; display:inline-flex; align-items:center; justify-content:center; }
.mr-cardstack .mr-card { position:absolute; transform:translateX(-3px); }
.mr-cardstack .mr-card + .mr-card { transform:translateX(3px); }

.mr-sub { display:inline-flex; align-items:center; gap:2px; font-weight:900; }
.mr-sub-up { color:#16a34a; }
.mr-sub-dn { color:#ef4444; }

/* Small devices: keep the split, just tighten spacing & columns */
@media (max-width: 720px) {
  .mr-grid { gap:8px; }
  .mr-item { grid-template-columns: 40px 20px minmax(0,1fr); gap:6px; }
  .mr-item--right { grid-template-columns: minmax(0,1fr) 20px 40px; }
  .mr-time { font-size:11px; }
  .mr-ico { width:20px; }
  .mr-team span { font-size:14px; }
}

/* Ultra-small (very narrow phones): still 2 columns, extra compact */
@media (max-width: 360px) {
  .mr-item { grid-template-columns: 36px 18px minmax(0,1fr); gap:5px; }
  .mr-item--right { grid-template-columns: minmax(0,1fr) 18px 36px; }
  .mr-sec-h { padding:6px 8px; }
}
`;